/**
 * A per-frame budget for art generated on the main thread (creature frames, scenery), so a new
 * sight never stalls a frame: past the budget, a picture already made stands in and the rest is
 * made in idle time, the most wanted first.
 */

/** Milliseconds of new art made while one animation frame is being painted. */
const BUDGET = 4;
let spent = 0;
let resetQueued = false;
/** How long one piece takes, on average lately: a piece only starts where it fits. */
let perPiece = 1;

/** Whether there is room left in this animation frame for another piece of new art. */
export function canDraw(): boolean {
  // One piece at least per animation frame, however long pieces take, so everything still fills in.
  return spent === 0 || spent + perPiece <= BUDGET;
}

function charge(ms: number): void {
  perPiece += (ms - perPiece) * 0.1;
  // No animation frames to share (a worker): everything is made as asked.
  if (typeof requestAnimationFrame === 'undefined') return;
  spent += ms;
  if (resetQueued) return;
  resetQueued = true;
  requestAnimationFrame(() => {
    spent = 0;
    resetQueued = false;
  });
}

/** Makes something now, counting its time against this animation frame's budget. */
export function timed<T>(make: () => T): T {
  const t0 = performance.now();
  const out = make();
  charge(performance.now() - t0);
  return out;
}

type Idle = (
  cb: (d: { timeRemaining(): number; didTimeout?: boolean }) => void,
  o?: { timeout: number },
) => number;
const idle: Idle =
  (globalThis as { requestIdleCallback?: Idle }).requestIdleCallback?.bind(globalThis) ??
  ((cb) => setTimeout(() => cb({ timeRemaining: () => 6 }), 16) as unknown as number);

const urgent: (() => void)[] = [];
const pending: (() => void)[] = [];
let pumping = false;

/**
 * Makes something in idle time. `first` puts it ahead of the rest (it is on screen now, standing
 * in for itself). Jobs left waiting still run: the pump keeps going until both queues are empty.
 */
export function later(job: () => void, first = false): void {
  (first ? urgent : pending).push(job);
  if (pumping) return;
  pumping = true;
  const pump = (d: { timeRemaining(): number; didTimeout?: boolean }) => {
    const left = () => urgent.length + pending.length;
    const next = () => timed((urgent.shift() ?? pending.shift())!);
    // What fits in the idle time left, and one at least when the browser has had none to give
    // for a while, so a busy game still catches up.
    if (d.didTimeout) next();
    while (left() && d.timeRemaining() > perPiece + 1) next();
    if (left()) idle(pump, { timeout: 100 });
    else pumping = false;
  };
  idle(pump, { timeout: 100 });
}

/**
 * `n` pictures made on first read under the budget: past it, `stand` (a picture of the same thing
 * already made, if any) is shown while the picture is made in idle time. With nothing to stand in,
 * the picture is made at once, so something always shows.
 */
export function lazyList<T extends object>(
  n: number,
  make: (i: number) => T,
  stand: () => T | undefined,
): T[] {
  const cache: (T | undefined)[] = new Array<T | undefined>(n);
  const queued = new Set<number>();
  const get = (i: number): T => {
    const c = cache[i];
    if (c) return c;
    const other = canDraw() ? undefined : (cache.find(Boolean) ?? stand());
    if (!other) return (cache[i] = timed(() => make(i)));
    if (!queued.has(i)) {
      queued.add(i);
      later(() => (cache[i] ??= make(i)), true);
    }
    return other;
  };
  return new Proxy(new Array<T>(n), {
    get(target, key, recv) {
      if (typeof key === 'string' && /^\d+$/.test(key)) {
        const i = Number(key);
        return i >= n ? undefined : get(i);
      }
      if (key === Symbol.iterator)
        return function* () {
          for (let i = 0; i < n; i++) yield get(i);
        };
      return Reflect.get(target, key, recv) as unknown;
    },
  });
}

/** One picture made on first read under the budget, with `stand` shown meanwhile (see `lazyList`). */
export function lazyOne<T extends object>(make: () => T, stand: () => T | undefined): () => T {
  const list = lazyList(1, make, stand);
  return () => list[0]!;
}

/** An object whose every property is made the first time it is read (and kept). */
export function lazyRecord<T>(makers: Readonly<Record<string, () => T>>): Record<string, T> {
  const out: Record<string, T> = {};
  for (const [k, make] of Object.entries(makers)) {
    let v: T | undefined;
    Object.defineProperty(out, k, { get: () => (v ??= make()), enumerable: true, configurable: true });
  }
  return out;
}
